/**
 * Store Hours Validation Utilities
 * 
 * Provides functions to check if a store is open and if delivery would
 * extend past store hours.
 * 
 * Important: Store hours are always evaluated in the STORE's timezone.
 * Display conversion to user timezone is handled in the UI components.
 */

import { getTimezoneForLocation } from './timezone';

// Days of week in order matching JavaScript's Date.getDay()
const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

type DayKey = typeof DAYS_OF_WEEK[number];

export interface DayHours {
  isOpen: boolean;
  openTime: string; // "HH:MM" format (24h)
  closeTime: string; // "HH:MM" format (24h)
  is24Hours?: boolean;
}

export interface StoreHours {
  [key: string]: DayHours | boolean | undefined;
  is24_7?: boolean;
}

export interface StoreHoursValidation {
  isOpen: boolean;
  closingTime: string | null; // "HH:MM" format or null if closed/24-7
  closingTimestamp: Date | null; // Full Date object for closing time today
  minutesUntilClose: number | null;
  reason?: string;
}

export interface VendorLocation {
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

/**
 * Get current time in a specific timezone as HH:MM string
 */
function getCurrentTimeInTimezone(timezone: string): string {
  try {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    // Fallback to local time if timezone is invalid
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }
}

/**
 * Get current day index (0-6) in a specific timezone
 */
function getCurrentDayInTimezone(timezone: string): number {
  try {
    const now = new Date();
    const dateInTz = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const day = dateInTz.getDay();
    // Validate the result is a valid day index
    if (isNaN(day) || day < 0 || day > 6) {
      return new Date().getDay();
    }
    return day;
  } catch {
    // Fallback to local timezone if timezone is invalid
    return new Date().getDay();
  }
}

export interface DeliveryTimeValidation {
  isValid: boolean;
  reason: string;
  storeClosingTime: string | null;
  estimatedDeliveryTime: string | null;
}

/**
 * Convert time string ("HH:MM") to minutes since midnight
 */
function timeToMinutes(timeStr: string): number {
  const parts = (timeStr || '00:00').split(':');
  const hours = parseInt(parts[0] || '0', 10);
  const minutes = parseInt(parts[1] || '0', 10);
  
  // Return 0 for invalid values
  if (isNaN(hours) || isNaN(minutes)) {
    return 0;
  }
  
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to time string ("HH:MM")
 */
function minutesToTime(minutes: number): string {
  if (isNaN(minutes)) {
    return '00:00';
  }
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Format time in 12-hour format for display
 */
export function formatTimeDisplay(timeStr: string): string {
  if (!timeStr) return '';
  
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  
  // Handle NaN or invalid values
  if (isNaN(hours) || isNaN(minutes)) return timeStr;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get the current day's hours from store delivery_hours
 */
export function getCurrentDayHours(deliveryHours: StoreHours | null | undefined): DayHours | null {
  if (!deliveryHours) return null;
  
  const now = new Date();
  const dayKey = DAYS_OF_WEEK[now.getDay()];
  const dayHours = deliveryHours[dayKey] as DayHours | undefined;
  
  return dayHours || null;
}

/**
 * Check if a store is currently open based on delivery_hours
 * 
 * @param deliveryHours - Store's delivery hours configuration
 * @param vendorLocation - Optional vendor location for timezone detection
 *                         If not provided, uses user's local timezone
 */
export function isStoreCurrentlyOpen(
  deliveryHours: StoreHours | null | undefined,
  vendorLocation?: VendorLocation
): StoreHoursValidation {
  // No hours set - assume closed
  if (!deliveryHours) {
    return {
      isOpen: false,
      closingTime: null,
      closingTimestamp: null,
      minutesUntilClose: null,
      reason: 'Store hours not configured',
    };
  }
  
  // If 24/7 is enabled, always open
  if (deliveryHours.is24_7) {
    return {
      isOpen: true,
      closingTime: null, // Always open, no closing time
      closingTimestamp: null,
      minutesUntilClose: null,
      reason: 'Open 24/7',
    };
  }
  
  // Get store's timezone based on location
  const storeTimezone = getTimezoneForLocation(
    vendorLocation?.city,
    vendorLocation?.state,
    vendorLocation?.country
  );
  
  // Get current day and time in the STORE's timezone
  const currentDayIndex = getCurrentDayInTimezone(storeTimezone);
  const dayKey = DAYS_OF_WEEK[currentDayIndex] || DAYS_OF_WEEK[new Date().getDay()];
  const dayHours = deliveryHours[dayKey] as DayHours | undefined;
  
  // Day not configured or marked as closed
  if (!dayHours || !dayHours.isOpen) {
    const dayName = dayKey ? dayKey.charAt(0).toUpperCase() + dayKey.slice(1) : 'today';
    return {
      isOpen: false,
      closingTime: null,
      closingTimestamp: null,
      minutesUntilClose: null,
      reason: `Store is closed on ${dayName}`,
    };
  }
  
  // If 24 hours for this day
  if (dayHours.is24Hours) {
    return {
      isOpen: true,
      closingTime: null,
      closingTimestamp: null,
      minutesUntilClose: null,
      reason: 'Open 24 hours today',
    };
  }
  
  // Get current time in store's timezone
  const currentTimeStr = getCurrentTimeInTimezone(storeTimezone);
  const currentMinutes = timeToMinutes(currentTimeStr);
  const openMinutes = timeToMinutes(dayHours.openTime);
  const closeMinutes = timeToMinutes(dayHours.closeTime);
  
  // Create closing timestamp for today (in user's local time for comparison)
  const now = new Date();
  const closingTimestamp = new Date(now);
  closingTimestamp.setHours(Math.floor(closeMinutes / 60), closeMinutes % 60, 0, 0);
  
  const minutesUntilClose = closeMinutes - currentMinutes;
  
  // Store hasn't opened yet
  if (currentMinutes < openMinutes) {
    return {
      isOpen: false,
      closingTime: dayHours.closeTime,
      closingTimestamp,
      minutesUntilClose: null,
      reason: `Store opens at ${formatTimeDisplay(dayHours.openTime)}`,
    };
  }
  
  // Store has already closed
  if (currentMinutes > closeMinutes) {
    return {
      isOpen: false,
      closingTime: dayHours.closeTime,
      closingTimestamp,
      minutesUntilClose: null,
      reason: `Store closed at ${formatTimeDisplay(dayHours.closeTime)}`,
    };
  }
  
  // Store is open
  return {
    isOpen: true,
    closingTime: dayHours.closeTime,
    closingTimestamp,
    minutesUntilClose,
    reason: minutesUntilClose <= 30 
      ? `Store closes in ${minutesUntilClose} minute${minutesUntilClose !== 1 ? 's' : ''}`
      : undefined,
  };
}

/**
 * Check if delivery ETA would extend past store closing hours
 * 
 * @param deliveryHours - Store's delivery hours configuration
 * @param dropoffEta - ISO timestamp of estimated delivery arrival
 * @param bufferMinutes - Extra buffer time to ensure store stays open after delivery (default 0)
 * @param vendorLocation - Optional vendor location for timezone detection
 * @returns Validation result indicating if delivery time is acceptable
 */
export function validateDeliveryTimeAgainstStoreHours(
  deliveryHours: StoreHours | null | undefined,
  dropoffEta: string | null | undefined,
  bufferMinutes: number = 0,
  vendorLocation?: VendorLocation
): DeliveryTimeValidation {
  // First check if store is currently open (using store's timezone)
  const storeStatus = isStoreCurrentlyOpen(deliveryHours, vendorLocation);
  
  if (!storeStatus.isOpen) {
    return {
      isValid: false,
      reason: storeStatus.reason || 'Store is currently closed',
      storeClosingTime: storeStatus.closingTime ? formatTimeDisplay(storeStatus.closingTime) : null,
      estimatedDeliveryTime: null,
    };
  }
  
  // If 24/7 or 24 hours today, no time constraint
  if (!storeStatus.closingTime || !storeStatus.closingTimestamp) {
    return {
      isValid: true,
      reason: 'Store is open 24 hours',
      storeClosingTime: null,
      estimatedDeliveryTime: dropoffEta ? formatDeliveryEta(dropoffEta) : null,
    };
  }
  
  // No delivery ETA provided - can't validate
  if (!dropoffEta) {
    return {
      isValid: true, // Allow to proceed, server will validate
      reason: 'Delivery time estimate not available',
      storeClosingTime: formatTimeDisplay(storeStatus.closingTime),
      estimatedDeliveryTime: null,
    };
  }
  
  // Parse delivery ETA
  const deliveryTime = new Date(dropoffEta);
  if (isNaN(deliveryTime.getTime())) {
    return {
      isValid: true, // Allow to proceed if we can't parse
      reason: 'Could not parse delivery time',
      storeClosingTime: formatTimeDisplay(storeStatus.closingTime),
      estimatedDeliveryTime: null,
    };
  }
  
  // Add buffer time to delivery
  const deliveryWithBuffer = new Date(deliveryTime.getTime() + bufferMinutes * 60 * 1000);
  
  // Compare delivery time with closing time
  if (deliveryWithBuffer > storeStatus.closingTimestamp) {
    const deliveryTimeStr = formatDeliveryEta(dropoffEta);
    
    return {
      isValid: false,
      reason: `The delivery process will be ongoing after the store closes at ${formatTimeDisplay(storeStatus.closingTime)}`,
      storeClosingTime: formatTimeDisplay(storeStatus.closingTime),
      estimatedDeliveryTime: deliveryTimeStr,
    };
  }
  
  // Delivery would arrive before store closes
  return {
    isValid: true,
    reason: 'Delivery will arrive during store hours',
    storeClosingTime: formatTimeDisplay(storeStatus.closingTime),
    estimatedDeliveryTime: formatDeliveryEta(dropoffEta),
  };
}

/**
 * Format delivery ETA for display
 */
function formatDeliveryEta(dropoffEta: string): string {
  const eta = new Date(dropoffEta);
  if (isNaN(eta.getTime())) return 'Unknown';
  
  const hours = eta.getHours();
  const minutes = eta.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Validate all vendor quotes against their store hours
 * Returns validation results for each vendor
 */
export interface VendorHoursValidation {
  vendorId: string;
  vendorName: string;
  isStoreOpen: boolean;
  isDeliveryTimeValid: boolean;
  reason: string;
  storeClosingTime: string | null;
  estimatedDeliveryTime: string | null;
}

export function validateAllVendorHours(
  vendorQuotes: Array<{
    vendorId: string;
    vendor_name: string;
    quote?: {
      dropoff_eta?: string;
    };
    delivery_hours?: StoreHours;
    error?: string;
    /** Vendor location for timezone detection */
    city?: string;
    state?: string;
    country?: string;
  }>
): VendorHoursValidation[] {
  return vendorQuotes.map(vq => {
    // Skip vendors with quote errors
    if (vq.error || !vq.quote) {
      return {
        vendorId: vq.vendorId,
        vendorName: vq.vendor_name,
        isStoreOpen: false,
        isDeliveryTimeValid: false,
        reason: vq.error || 'No delivery quote available',
        storeClosingTime: null,
        estimatedDeliveryTime: null,
      };
    }
    
    // Build vendor location for timezone detection
    const vendorLocation: VendorLocation = {
      city: vq.city,
      state: vq.state,
      country: vq.country,
    };
    
    const deliveryValidation = validateDeliveryTimeAgainstStoreHours(
      vq.delivery_hours,
      vq.quote.dropoff_eta,
      0,
      vendorLocation
    );
    
    return {
      vendorId: vq.vendorId,
      vendorName: vq.vendor_name,
      isStoreOpen: deliveryValidation.isValid || deliveryValidation.reason.includes('24'),
      isDeliveryTimeValid: deliveryValidation.isValid,
      reason: deliveryValidation.reason,
      storeClosingTime: deliveryValidation.storeClosingTime,
      estimatedDeliveryTime: deliveryValidation.estimatedDeliveryTime,
    };
  });
}

